import React from "react";
import RevealAnimation from "../RevealAnimation";
import { Link } from "gatsby";
import "./articlesHeroBanner.scss";

/**
 * Unique hero banner for /insights/articles
 * Warm editorial theme with red/coral accents
 */
function ArticlesHeroBanner({
  title = "Articles",
  subtitle = "",
  breadcrumbs = [],
  totalArticles = 0,
  totalTopics = 0,
}) {
  return (
    <section className="articles-hero-banner">
      {/* Background */}
      <div className="articles-hero-bg">
        <div className="hero-gradient-mesh" />
        <div className="hero-dot-pattern" />
        <div className="hero-warm-glow hero-warm-glow--1" />
        <div className="hero-warm-glow hero-warm-glow--2" />
        {/* Decorative lines */}
        <div className="hero-deco-lines">
          <div className="deco-line deco-line--1" />
          <div className="deco-line deco-line--2" />
          <div className="deco-line deco-line--3" />
        </div>
      </div>

      <div className="articles-hero-inner body-container">
        {/* Breadcrumbs */}
        {breadcrumbs.length > 0 && (
          <RevealAnimation
            component="nav"
            className="articles-hero-breadcrumbs"
            delay={0}
            animation="fade"
          >
            {breadcrumbs.map((crumb, i) => {
              const isLast = i === breadcrumbs.length - 1;
              return (
                <React.Fragment key={i}>
                  {isLast ? (
                    <span className="crumb crumb--active">{crumb.label}</span>
                  ) : (
                    <>
                      <Link to={crumb.to} className="crumb">
                        {crumb.label}
                      </Link>
                      <span className="crumb-sep">/</span>
                    </>
                  )}
                </React.Fragment>
              );
            })}
          </RevealAnimation>
        )}

        <div className="articles-hero-content">
          <div className="articles-hero-left">
            {/* Category badge */}
            <RevealAnimation
              component="div"
              className="articles-category-badge"
              delay={0.05}
              animation="fade"
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                <polyline points="14 2 14 8 20 8" />
                <line x1="16" y1="13" x2="8" y2="13" />
                <line x1="16" y1="17" x2="8" y2="17" />
              </svg>
              Articles & Insights
            </RevealAnimation>

            {/* Title */}
            <RevealAnimation
              component="h1"
              className="articles-hero-title"
              delay={0.1}
              animation="slide-up"
            >
              Expert <span className="title-accent">Articles</span> &<br />
              Industry Perspectives
            </RevealAnimation>

            {/* Subtitle */}
            {subtitle && (
              <RevealAnimation
                component="p"
                className="articles-hero-subtitle"
                delay={0.2}
                animation="slide-up"
              >
                {subtitle}
              </RevealAnimation>
            )}

            {/* Nav pills */}
            <RevealAnimation
              component="div"
              className="articles-hero-nav"
              delay={0.25}
              animation="slide-up"
            >
              <Link
                to="/insights/articles"
                className="hero-pill hero-pill--active"
              >
                <span className="pill-dot" />
                Articles
              </Link>
              <Link to="/insights/white-papers" className="hero-pill">
                White Papers
              </Link>
              <Link to="/insights" className="hero-pill">
                All Insights
              </Link>
            </RevealAnimation>
          </div>

          {/* Right side - editorial visual */}
          <div className="articles-hero-right">
            <RevealAnimation
              className="articles-hero-visual"
              delay={0.3}
              animation="fade"
            >
              {/* Stacked card visual */}
              <div className="editorial-stack">
                <div className="stack-card stack-card--back">
                  <div className="stack-card-line stack-card-line--wide" />
                  <div className="stack-card-line stack-card-line--medium" />
                  <div className="stack-card-line stack-card-line--short" />
                </div>
                <div className="stack-card stack-card--mid">
                  <div className="stack-card-line stack-card-line--wide" />
                  <div className="stack-card-line stack-card-line--medium" />
                  <div className="stack-card-line stack-card-line--short" />
                </div>
                <div className="stack-card stack-card--front">
                  <div className="stack-card-header">
                    <div className="stack-card-avatar" />
                    <div className="stack-card-meta">
                      <div className="stack-card-line stack-card-line--name" />
                      <div className="stack-card-line stack-card-line--date" />
                    </div>
                  </div>
                  <div className="stack-card-title-line" />
                  <div className="stack-card-title-line stack-card-title-line--short" />
                  <div className="stack-card-body">
                    <div className="stack-card-line stack-card-line--wide" />
                    <div className="stack-card-line stack-card-line--wide" />
                    <div className="stack-card-line stack-card-line--medium" />
                  </div>
                  <div className="stack-card-tag">
                    <span />
                    <span />
                  </div>
                </div>
              </div>

              {/* Floating stats */}
              {(totalArticles > 0 || totalTopics > 0) && (
                <div className="editorial-floating-stats">
                  {totalArticles > 0 && (
                    <div className="floating-stat floating-stat--articles">
                      <span className="floating-stat-value">
                        {totalArticles}+
                      </span>
                      <span className="floating-stat-label">Articles</span>
                    </div>
                  )}
                  {totalTopics > 0 && (
                    <div className="floating-stat floating-stat--topics">
                      <span className="floating-stat-value">
                        {totalTopics}+
                      </span>
                      <span className="floating-stat-label">Topics</span>
                    </div>
                  )}
                </div>
              )}
            </RevealAnimation>
          </div>
        </div>
      </div>

      {/* Bottom wave accent */}
      <div className="articles-hero-bottom">
        <svg
          viewBox="0 0 1440 60"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          preserveAspectRatio="none"
        >
          <path
            d="M0 60V30C240 10 480 0 720 10C960 20 1200 40 1440 30V60H0Z"
            fill="white"
          />
        </svg>
      </div>
    </section>
  );
}

export default ArticlesHeroBanner;
